/**
 * Sidebar panel that shows the attached folder's tree once the user has
 * granted access. Folder rows expand on click via the File System Access
 * API; `.gitignore` and the built-in denylist hide noisy entries. Single-
 * click selects a file (orange-accent row); double-click opens it in the
 * local-file viewer (the side pane that mirrors the artifact canvas).
 *
 * @module file-agent/components/file-tree-panel
 */

import { ChevronDown, ChevronRight, ChevronsDownUp, Folder, FolderOpen, RefreshCw, Search } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { compileGitignore, isIgnored, type Pattern } from "../lib/gitignore";
import { getFileBadge } from "../lib/file-badge";
import { detectLocalFileKind, useLocalFileViewerStore } from "../store/local-file-viewer-store";
import type { FolderAttachment } from "../lib/use-folder-attachment";

interface TreeNode {
  name: string;
  path: string;
  kind: "file" | "directory";
  children: TreeNode[] | null;
  expanded: boolean;
}

interface FileTreePanelProps {
  attachment: FolderAttachment;
  className?: string;
}

/**
 * Tree view of the attached folder. Lazy-loads each directory's children
 * on first expand so opening the chat doesn't pay a full traversal up-front.
 */
export function FileTreePanel({ attachment, className }: FileTreePanelProps) {
  const { handle, folderName, needsReconnect } = attachment;
  const [root, setRoot] = useState<TreeNode | null>(null);
  const patternsRef = useRef<Pattern[]>([]);
  const [search, setSearch] = useState("");
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!handle || needsReconnect) {
      setRoot(null);
      return;
    }
    try {
      const file = await handle.getFileHandle(".gitignore");
      const text = await (await file.getFile()).text();
      patternsRef.current = compileGitignore(text);
    } catch {
      patternsRef.current = [];
    }
    const initial: TreeNode = {
      name: handle.name,
      path: "",
      kind: "directory",
      children: null,
      expanded: true,
    };
    initial.children = await readDirectoryEntries(handle, "", patternsRef.current);
    setRoot(initial);
  }, [handle, needsReconnect]);

  useEffect(() => {
    reload();
  }, [reload]);

  const toggleNode = useCallback(
    async (path: string) => {
      if (!root || !handle) return;
      const updated = await toggleAndLoad(root, path, handle, patternsRef.current);
      setRoot({ ...updated });
    },
    [root, handle],
  );

  const collapseAll = useCallback(() => {
    if (!root) return;
    setRoot({ ...collapseRecursively(root, true) });
  }, [root]);

  const openFile = useLocalFileViewerStore((s) => s.open);

  const handleFileDoubleClick = useCallback(
    async (node: TreeNode) => {
      if (!handle || node.kind !== "file") return;
      try {
        const fileHandle = await resolveFileHandle(handle, node.path);
        openFile({
          name: node.name,
          path: node.path,
          kind: detectLocalFileKind(node.name),
          handle: fileHandle,
        });
      } catch (err) {
        // Swallow — the row stays selected. A toast surfaces the error if
        // it ever recurs in practice; for v1 we keep the panel quiet.
        console.error("Failed to open local file:", err);
      }
    },
    [handle, openFile],
  );

  if (!handle) {
    return (
      <div className={cn("h-full bg-background", className)}>
        <p className="px-4 py-3 text-sm text-muted-foreground">
          Attach a local folder to view files.
        </p>
      </div>
    );
  }

  if (needsReconnect) {
    return (
      <div className={cn("h-full bg-background", className)}>
        <p className="px-4 py-3 text-sm text-muted-foreground">
          Reconnect to <strong>{folderName}</strong> to view files.
        </p>
      </div>
    );
  }

  if (!root) {
    return (
      <div className={cn("h-full bg-background", className)}>
        <p className="px-4 py-3 text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className={cn("flex h-full flex-col bg-background", className)}>
      {/* Folder header chrome */}
      <div className="border-b border-border/60 bg-muted/30">
        <div className="flex h-12 items-center justify-between px-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <FolderOpen className="h-5 w-5 shrink-0 text-primary" />
            <span className="truncate text-sm font-medium text-foreground">
              {folderName ?? handle.name}
            </span>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-success" />
            Connected
          </span>
        </div>
        <div className="flex items-center gap-1.5 px-4 pb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files…"
              className="h-9 w-full rounded-md border border-border/60 bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/10"
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={reload}
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={collapseAll}
            title="Collapse all"
          >
            <ChevronsDownUp className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-auto py-2">
        <TreeNodeView
          node={root}
          depth={0}
          onToggle={toggleNode}
          onSelect={setSelectedPath}
          onOpen={handleFileDoubleClick}
          selectedPath={selectedPath}
          search={search.trim().toLowerCase()}
          isRoot
        />
      </div>

      {/* Hint footer */}
      <div className="flex shrink-0 items-center gap-1.5 border-t border-border/60 bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
        Single-click to select · Double-click to open
      </div>
    </div>
  );
}

interface TreeNodeViewProps {
  node: TreeNode;
  depth: number;
  onToggle: (path: string) => void;
  onSelect: (path: string) => void;
  onOpen: (node: TreeNode) => void;
  selectedPath: string | null;
  search: string;
  isRoot?: boolean;
}

function TreeNodeView({
  node,
  depth,
  onToggle,
  onSelect,
  onOpen,
  selectedPath,
  search,
  isRoot,
}: TreeNodeViewProps) {
  // Skip rendering the synthetic root node — its children mount at depth 0.
  if (isRoot) {
    return (
      <>
        {node.children?.map((child) => (
          <TreeNodeView
            key={child.path}
            node={child}
            depth={0}
            onToggle={onToggle}
            onSelect={onSelect}
            onOpen={onOpen}
            selectedPath={selectedPath}
            search={search}
          />
        ))}
      </>
    );
  }

  const matchesSearch = !search || node.name.toLowerCase().includes(search);
  // For directories, also surface descendants matching the search by
  // keeping the row visible if any child matches.
  const subtreeMatches =
    node.kind === "directory" && search
      ? subtreeContainsMatch(node, search)
      : false;
  if (search && !matchesSearch && !subtreeMatches) return null;

  const isSelected = selectedPath === node.path;
  const badge = node.kind === "file" ? getFileBadge(node.name) : null;
  const Chevron = node.expanded ? ChevronDown : ChevronRight;
  const FolderIcon = node.expanded ? FolderOpen : Folder;

  const handleClick = () => {
    if (node.kind === "directory") {
      onToggle(node.path);
    } else {
      onSelect(node.path);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleClick}
        onDoubleClick={() => node.kind === "file" && onOpen(node)}
        className={cn(
          "relative flex w-full items-center gap-2 py-1 pr-3 text-left text-sm transition-colors",
          "h-8",
          isSelected
            ? "bg-primary/10 font-medium text-foreground"
            : "text-foreground/80 hover:bg-muted/60",
        )}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
      >
        {isSelected && (
          <span className="pointer-events-none absolute inset-y-0 left-0 w-[2px] bg-primary" />
        )}
        {node.kind === "directory" ? (
          <Chevron className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        {node.kind === "directory" ? (
          <FolderIcon
            className={cn(
              "h-4 w-4 shrink-0",
              node.expanded ? "text-primary" : "text-muted-foreground",
            )}
          />
        ) : (
          <span
            className={cn(
              "inline-flex h-4 min-w-[18px] shrink-0 items-center justify-center rounded-sm px-1 font-mono text-[9px] font-semibold tracking-tight",
              badge!.className,
            )}
          >
            {badge!.label}
          </span>
        )}
        <span className="truncate">{node.name}</span>
      </button>
      {node.expanded && node.children && (
        <div>
          {node.children.map((child) => (
            <TreeNodeView
              key={child.path}
              node={child}
              depth={depth + 1}
              onToggle={onToggle}
              onSelect={onSelect}
              onOpen={onOpen}
              selectedPath={selectedPath}
              search={search}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** True when any descendant node's name contains the search term. */
function subtreeContainsMatch(node: TreeNode, term: string): boolean {
  if (node.name.toLowerCase().includes(term)) return true;
  if (!node.children) return false;
  return node.children.some((c) => subtreeContainsMatch(c, term));
}

async function readDirectoryEntries(
  dir: FileSystemDirectoryHandle,
  parentPath: string,
  patterns: Pattern[],
): Promise<TreeNode[]> {
  const out: TreeNode[] = [];
  const iter = dir as unknown as AsyncIterable<[string, FileSystemHandle]>;
  for await (const [name, child] of iter) {
    const path = parentPath ? `${parentPath}/${name}` : name;
    const isDir = child.kind === "directory";
    if (isIgnored(path, patterns, isDir)) continue;
    out.push({
      name,
      path,
      kind: isDir ? "directory" : "file",
      children: null,
      expanded: false,
    });
  }
  out.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "directory" ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  return out;
}

async function toggleAndLoad(
  node: TreeNode,
  targetPath: string,
  rootHandle: FileSystemDirectoryHandle,
  patterns: Pattern[],
): Promise<TreeNode> {
  if (node.path === targetPath && node.kind === "directory") {
    if (node.children == null) {
      const dir = await resolveDirectory(rootHandle, node.path);
      node.children = await readDirectoryEntries(dir, node.path, patterns);
    }
    node.expanded = !node.expanded;
    return node;
  }
  if (node.children) {
    node.children = await Promise.all(
      node.children.map((child) =>
        targetPath === child.path || targetPath.startsWith(`${child.path}/`)
          ? toggleAndLoad(child, targetPath, rootHandle, patterns)
          : Promise.resolve(child),
      ),
    );
  }
  return node;
}

/** Recursively force every directory under `node` to expanded=false. */
function collapseRecursively(node: TreeNode, isRoot: boolean): TreeNode {
  const next: TreeNode = { ...node };
  if (next.kind === "directory" && !isRoot) {
    next.expanded = false;
  }
  if (next.children) {
    next.children = next.children.map((c) => collapseRecursively(c, false));
  }
  return next;
}

async function resolveDirectory(
  root: FileSystemDirectoryHandle,
  path: string,
): Promise<FileSystemDirectoryHandle> {
  if (!path) return root;
  let dir = root;
  for (const seg of path.split("/").filter(Boolean)) {
    dir = await dir.getDirectoryHandle(seg);
  }
  return dir;
}

/** Walk to a file path relative to the root and return its handle. */
async function resolveFileHandle(
  root: FileSystemDirectoryHandle,
  path: string,
): Promise<FileSystemFileHandle> {
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) {
    throw new Error("Path is empty");
  }
  const fileName = segments.pop()!;
  let dir = root;
  for (const seg of segments) {
    dir = await dir.getDirectoryHandle(seg);
  }
  return dir.getFileHandle(fileName);
}
